import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { 
  Settings, 
  Type, 
  Contrast, 
  Sun, 
  Moon, 
  Minus, 
  Plus, 
  RotateCcw, 
  Keyboard,
  Eye,
  Volume2
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'

interface AccessibilitySettings {
  fontSize: number
  highContrast: boolean
  darkMode: boolean
  reducedMotion: boolean
  focusVisible: boolean
  screenReaderMode: boolean
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  highContrast: false,
  darkMode: false,
  reducedMotion: false,
  focusVisible: true,
  screenReaderMode: false
}

export const AccessibilityPanel = () => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings)
  const [isOpen, setIsOpen] = useState(false)

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings')
    if (saved) {
      setSettings(JSON.parse(saved))
    }
    
    // Check if panel should auto-open (only first time)
    const hasSeenPanel = localStorage.getItem('accessibility-panel-seen')
    if (!hasSeenPanel) {
      // Auto-open on first visit after a short delay
      const timer = setTimeout(() => {
        setIsOpen(true)
        localStorage.setItem('accessibility-panel-seen', 'true')
      }, 2000) // 2 second delay
      
      return () => clearTimeout(timer)
    }
  }, [])

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement

    // Font size
    root.style.fontSize = `${settings.fontSize}%`

    // High contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast')
    } else {
      root.classList.remove('high-contrast')
    }

    // Dark mode
    if (settings.darkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Reduced motion
    if (settings.reducedMotion) {
      root.classList.add('reduce-motion')
    } else {
      root.classList.remove('reduce-motion')
    }

    // Enhanced focus
    if (settings.focusVisible) {
      root.classList.add('enhanced-focus')
    } else {
      root.classList.remove('enhanced-focus')
    }

    // Screen reader mode
    if (settings.screenReaderMode) {
      root.classList.add('screen-reader-mode')
    } else {
      root.classList.remove('screen-reader-mode')
    }

    // Save to localStorage
    localStorage.setItem('accessibility-settings', JSON.stringify(settings))
  }, [settings])

  const updateSetting = (key: keyof AccessibilitySettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const resetSettings = () => {
    setSettings(defaultSettings)
  }

  const increaseFontSize = () => {
    const newSize = Math.min(settings.fontSize + 10, 150)
    updateSetting('fontSize', newSize)
  }

  const decreaseFontSize = () => {
    const newSize = Math.max(settings.fontSize - 10, 80)
    updateSetting('fontSize', newSize)
  }

  return (
    <>
      {/* Floating Accessibility Button */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="fixed bottom-4 left-4 z-50 rounded-full shadow-lg bg-background border-2 border-primary hover:bg-primary hover:text-primary-foreground md:bottom-6 md:left-6"
            aria-label="Barrierefreiheit-Einstellungen öffnen"
            title="Barrierefreiheit-Einstellungen"
          >
            <Settings className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        
        <SheetContent side="left" className="w-80 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Barrierefreiheit
            </SheetTitle>
            <SheetDescription>
              Passen Sie die Darstellung an Ihre Bedürfnisse an
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            
            {/* Font Size Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Type className="h-4 w-4" />
                  Schriftgröße
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={decreaseFontSize}
                    aria-label="Schrift verkleinern"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  
                  <span className="text-sm font-medium" aria-live="polite">
                    {settings.fontSize}%
                  </span>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={increaseFontSize}
                    aria-label="Schrift vergrößern"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                <Slider
                  value={[settings.fontSize]}
                  onValueChange={([value]) => updateSetting('fontSize', value)}
                  min={80}
                  max={150}
                  step={10}
                  className="w-full"
                  aria-label="Schriftgröße anpassen"
                />
              </CardContent>
            </Card>

            {/* Visual Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Contrast className="h-4 w-4" />
                  Darstellung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label htmlFor="high-contrast" className="text-sm font-medium">
                      Hoher Kontrast
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Verbessert die Lesbarkeit
                    </p>
                  </div>
                  <Switch
                    id="high-contrast"
                    checked={settings.highContrast}
                    onCheckedChange={(checked) => updateSetting('highContrast', checked)}
                    aria-describedby="high-contrast-description"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label htmlFor="dark-mode" className="text-sm font-medium flex items-center gap-2">
                      {settings.darkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                      Dunkler Modus
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Reduziert Augenbelastung
                    </p>
                  </div>
                  <Switch
                    id="dark-mode"
                    checked={settings.darkMode}
                    onCheckedChange={(checked) => updateSetting('darkMode', checked)}
                  />
                </div>

              </CardContent>
            </Card>

            {/* Motion & Animation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Keyboard className="h-4 w-4" />
                  Bewegung & Navigation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label htmlFor="reduced-motion" className="text-sm font-medium">
                      Bewegungen reduzieren
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Minimiert Animationen
                    </p>
                  </div>
                  <Switch
                    id="reduced-motion"
                    checked={settings.reducedMotion}
                    onCheckedChange={(checked) => updateSetting('reducedMotion', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label htmlFor="focus-visible" className="text-sm font-medium">
                      Verstärkte Fokus-Anzeige
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Bessere Tastaturnavigation
                    </p>
                  </div>
                  <Switch
                    id="focus-visible"
                    checked={settings.focusVisible}
                    onCheckedChange={(checked) => updateSetting('focusVisible', checked)}
                  />
                </div>

              </CardContent>
            </Card>

            {/* Screen Reader */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Volume2 className="h-4 w-4" />
                  Screenreader
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label htmlFor="screen-reader" className="text-sm font-medium">
                      Screenreader-Modus
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Optimiert für Vorlese-Software
                    </p>
                  </div>
                  <Switch
                    id="screen-reader"
                    checked={settings.screenReaderMode}
                    onCheckedChange={(checked) => updateSetting('screenReaderMode', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Reset Button */}
            <Button
              variant="outline"
              onClick={resetSettings}
              className="w-full"
              aria-label="Alle Einstellungen zurücksetzen"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Zurücksetzen
            </Button>

            {/* Keyboard Shortcuts Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Tastenkombinationen</CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <div className="flex justify-between">
                  <span>Barrierefreiheit öffnen:</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs">Alt + A</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Schrift vergrößern:</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl + +</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Schrift verkleinern:</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl + -</kbd>
                </div>
              </CardContent>
            </Card>

          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

// Keyboard shortcuts handler
export const useAccessibilityShortcuts = () => {
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Alt + A to open accessibility panel
      if (e.altKey && e.key === 'a') {
        e.preventDefault()
        const trigger = document.querySelector('[aria-label="Barrierefreiheit-Einstellungen öffnen"]') as HTMLButtonElement
        if (trigger) {
          trigger.click()
        }
      }

      // Ctrl + Plus to increase font size
      if (e.ctrlKey && (e.key === '+' || e.key === '=')) {
        e.preventDefault()
        const root = document.documentElement
        const currentSize = parseInt(root.style.fontSize) || 100
        const newSize = Math.min(currentSize + 10, 150)
        root.style.fontSize = `${newSize}%`
      }

      // Ctrl + Minus to decrease font size
      if (e.ctrlKey && e.key === '-') {
        e.preventDefault()
        const root = document.documentElement
        const currentSize = parseInt(root.style.fontSize) || 100
        const newSize = Math.max(currentSize - 10, 80)
        root.style.fontSize = `${newSize}%`
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [])
}